import React, { useState, useEffect, useMemo, useRef } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";
import ngeohash from "ngeohash";
import { GeoPoint } from "firebase/firestore";
import {
  getPlacesLibrary,
  fetchAutocompletePredictions,
  fetchPlaceDetailsNew,
  normalizePlaceDetails,
  createSessionToken,
} from "../utils/googleMapsLoader";

const QTDetailsLocationWithLabelSection = ({
  label,
  value,
  onChange,
  disabled,
  editEnabled,
  locationDisplayValue,
  quicktaleDetails,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [displayLocationValue, setDisplayLocationValue] = useState(locationDisplayValue || "");
  // Surfaces the real reason the dropdown is empty instead of always claiming
  // "No locations" (e.g. an invalid key returns REQUEST_DENIED with no results).
  const [lookupError, setLookupError] = useState("");
  const [servicesReady, setServicesReady] = useState(false);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    setDisplayLocationValue(locationDisplayValue || "");
  }, [locationDisplayValue]);

  useEffect(() => {
    let cancelled = false;

    getPlacesLibrary()
      .then(() => {
        if (cancelled) return;
        setServicesReady(true);
        setLookupError("");
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Google Maps Places library failed to load:", error);
        setLookupError("Location lookup unavailable");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const fetch = useMemo(
    () =>
      debounce((input, callback) => {
        fetchAutocompletePredictions(input, {
          sessionToken: sessionTokenRef.current,
        })
          .then((predictions) => callback(predictions, null))
          .catch((error) => callback(null, error));
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    // One session token per typing session; it is consumed by the details
    // request made when a suggestion is picked.
    if (!sessionTokenRef.current) {
      createSessionToken()
        .then((token) => {
          sessionTokenRef.current = token;
        })
        .catch(() => {
          // Session tokens are a billing optimization only; ignore failures.
        });
    }

    fetch(inputValue, (results, error) => {
      if (!active) return;

      if (error) {
        // Previously the status argument was dropped entirely, so a failure
        // looked identical to "no matching places".
        console.warn(
          `Places autocomplete failed for "${inputValue}":`,
          error?.message || error
        );
        setLookupError("Location lookup unavailable");
      } else {
        setLookupError("");
      }

      let newOptions = [];

      if (value) {
        newOptions = [value];
      }

      if (results) {
        newOptions = [...newOptions, ...results];
      }

      setOptions(newOptions);
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, servicesReady]);

  const fetchPlaceDetails = async (placeId, prediction) => {
    const place = await fetchPlaceDetailsNew(placeId, prediction);
    const { locationDisplay, locationCountry, latitude, longitude } =
      normalizePlaceDetails(place);

    if (typeof latitude !== "number" || typeof longitude !== "number") {
      throw new Error(
        `Place ${placeId} returned no usable coordinates.`
      );
    }

    const geohash = ngeohash.encode(latitude, longitude);
    const geopoint = new GeoPoint(latitude, longitude);

    return {
      coordinates: geopoint,
      g: { geohash, geopoint },
      locationDisplay,
      locationCountry,
    };
  };

  return (
    <Grid container alignItems={"center"} style={{ marginBottom: 24 }}>
      <span style={titleTextStyle}>{label}</span>
      <Autocomplete
        id="google-map-demo"
        popupIcon={null}
        sx={{ width: "70%" }}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        noOptionsText={lookupError || "No locations"}
        onChange={async (event, newValue) => {
          try {
            setOptions(newValue ? [newValue, ...options] : options);
            setInputValue(newValue?.description || "");

            if (newValue && newValue.place_id) {
              try {
                const details = await fetchPlaceDetails(
                  newValue.place_id,
                  newValue
                );
                // The token is consumed by the details request; start a new
                // session for the next search.
                sessionTokenRef.current = null;
                setDisplayLocationValue(details.locationDisplay || "");
                onChange({
                  location: newValue.description,
                  locationDisplay: details.locationDisplay, // Include locationDisplay
                  locationCountry: details.locationCountry, // Include locationDisplay
                  ...details,
                });
              } catch (error) {
                console.error("Error fetching place details:", error);
              }
            } else {
              onChange(newValue);
            }
          } catch (error) {
            console.error("Error changing location data:", error);
          }
        }}
        // onChange={async (event, newValue) => {
        //   setOptions(newValue ? [newValue, ...options] : options);
        //   setInputValue(newValue?.description || "");

        //   if (newValue && newValue.place_id) {
        //     try {
        //       const details = await fetchPlaceDetails(newValue.place_id);
        //       onChange({
        //         location: newValue.description,
        //         ...details,
        //       });
        //     } catch (error) {
        //       console.error("Error fetching place details:", error);
        //     }
        //   } else {
        //     onChange(newValue);
        //   }
        // }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={label}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#000000",
              },
              backgroundColor: editEnabled ? "#D2D2D2" : "#FFFFFF",
              color: "black",
            }}
            InputProps={{
              ...params.InputProps,
              //   type: "search",
              sx: {
                ...textfieldSXStyles,
                height:  52,
                backgroundColor: editEnabled ? "#D2D2D2" : "#FFFFFF",
              },
            }}
          />
        )}
        renderOption={(props, option) => {
          // A persisted value (plain string or object without
          // structured_formatting) can be injected into `options`, so guard it.
          const structured =
            typeof option === "string" ? null : option?.structured_formatting;
          const mainText =
            structured?.main_text ||
            (typeof option === "string" ? option : option?.description) ||
            "";
          const secondaryText = structured?.secondary_text || "";
          const matches = structured?.main_text_matched_substrings || [];

          const parts = parse(
            mainText,
            matches.map((match) => [match.offset, match.offset + match.length])
          );

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: "flex", width: 44 }}>
                  <LocationOnIcon sx={{ color: "text.secondary" }} />
                </Grid>
                <Grid
                  item
                  sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
                >
                  {parts.map((part, index) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                    >
                      {part.text}
                    </Box>
                  ))}
                  <Typography variant="body2" color="text.secondary">
                    {secondaryText}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
        disabled={editEnabled ? false : disabled}
      />
      
      {/* Display Location Field */}
      <Grid container alignItems={"center"} style={{ marginBottom: 0, marginTop: 24 }}>
        <span style={titleTextStyle}>Display Location</span>
        <TextField
          variant="outlined"
          disabled={editEnabled ? false : disabled}
          placeholder="Display Location"
          value={displayLocationValue}
          onChange={(e) => {
            const newDisplayValue = e.target.value;
            setDisplayLocationValue(newDisplayValue);
            // Only update locationDisplay, preserve all existing location data from quicktaleDetails
            onChange({
              location: quicktaleDetails?.location || value?.description || value,
              locationDisplay: newDisplayValue,
              // Preserve existing coordinates, geohash, and country from quicktaleDetails
              coordinates: quicktaleDetails?.coordinates,
              g: quicktaleDetails?.g,
              locationCountry: quicktaleDetails?.locationCountry,
            });
          }}
          sx={{
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#000000",
            },
            width: "70%",
          }}
          InputProps={{
            sx: {
              height: 40,
              border: "none",
              borderColor: "#7676801F",
              color: "#515563",
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "Avenir Book, sans-serif",
              backgroundColor: editEnabled ? "#D2D2D2" : "#FFFFFF",
            },
          }}
        />
      </Grid>
    </Grid>
  );
};

export default QTDetailsLocationWithLabelSection;

const titleTextStyle = {
  width: "25%",
  color: "#020303",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
};

const textfieldSXStyles = {
  height: 40,
  border: "none",
  borderColor: "#7676801F",
  color: "#515563",
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "Avenir Book, sans-serif",
  //paddingBottom: 2,
};
